// Device lifecycle: a browser-only singleton adapter/device cache.

let _device = null;
let _adapter = null;

/**
 * Requests a WebGPU adapter/device and caches them. Calling this again before
 * `cleanup()` returns the same device rather than requesting a new one.
 * @param {{ powerPreference?: "high-performance"|"low-power" }} [options]
 * @returns {Promise<GPUDevice>}
 * @throws {Error} if WebGPU is unsupported or no adapter is available
 */
export async function init({ powerPreference = "high-performance" } = {}) {
  if (_device) return _device;

  if (!navigator.gpu) {
    throw new Error("WebGPU is not supported in this browser.");
  }

  _adapter =
    (await navigator.gpu.requestAdapter({ powerPreference })) ??
    (await navigator.gpu.requestAdapter());
  if (!_adapter) {
    throw new Error("No WebGPU adapter found.");
  }

  _device = await _adapter.requestDevice();
  // Fires for any GPU error not caught by a pushErrorScope/popErrorScope pair.
  // See: https://developer.mozilla.org/en-US/docs/Web/API/GPUDevice/uncapturederror_event
  _device.addEventListener("uncapturederror", (e) => {
    console.error("Uncaptured GPU error:", e.error.message);
  });

  return _device;
}

/** Destroys the cached device so a later `init()` call requests a fresh one. */
export function cleanup() {
  if (_device) {
    _device.destroy();
    _device = null;
  }
  _adapter = null;
}

/**
 * @returns {string} human-readable adapter description, for display in the UI
 * @throws {Error} if `init()` has not been called
 */
export function gpuName() {
  if (!_adapter) {
    throw new Error("WebGPU adapter not initialized — call init() first.");
  }
  const { device, description } = _adapter.info;
  return description || device || "unknown GPU";
}

/**
 * @returns {GPUDevice}
 * @throws {Error} if `init()` has not been called
 */
export function getDevice() {
  if (!_device) {
    throw new Error("WebGPU device not initialized — call init() first.");
  }
  return _device;
}
